/**
 * Resolve a web UI "edit" URL for catalog entities (GitLab/GitHub) when
 * backstage.io/edit-url is missing — matches catalog table + About card behavior.
 */

import {
  ANNOTATION_EDIT_URL,
  ANNOTATION_SOURCE_LOCATION,
  ANNOTATION_VIEW_URL,
} from '@backstage/catalog-model';

export function deriveScmWebEditUrl(rawUrl: string): string | undefined {
  const trimmed = rawUrl.trim();
  if (!trimmed || trimmed.startsWith('file:')) return undefined;
  const u = trimmed;
  try {
    const parsed = new URL(u);
    const host = parsed.hostname.toLowerCase();
    if (host.includes('gitlab')) {
      if (u.includes('/-/blob/')) return u.replace('/-/blob/', '/-/edit/');
      if (u.includes('/-/tree/')) return u.replace('/-/tree/', '/-/edit/');
      return undefined;
    }
    if (host === 'github.com' || host.endsWith('.github.com')) {
      if (u.includes('/blob/')) return u.replace('/blob/', '/edit/');
      if (u.includes('/tree/')) return u.replace('/tree/', '/edit/');
    }
  } catch {
    return undefined;
  }
  return undefined;
}

export function resolveCatalogWebEditUrl(entity: {
  metadata?: { annotations?: Record<string, string> };
}): string | undefined {
  const ann = entity.metadata?.annotations ?? {};
  const explicit = ann[ANNOTATION_EDIT_URL]?.trim();
  if (explicit) return explicit;

  const sourceLoc = ann[ANNOTATION_SOURCE_LOCATION]?.trim();
  if (sourceLoc?.startsWith('url:')) {
    const derived = deriveScmWebEditUrl(sourceLoc.slice(4).trim());
    if (derived) return derived;
  }

  const viewUrl = ann[ANNOTATION_VIEW_URL]?.trim();
  if (viewUrl) {
    const derived = deriveScmWebEditUrl(viewUrl);
    if (derived) return derived;
  }

  return undefined;
}
